import React from 'react'
import { Field } from 'redux-form'
import {
  makeStyles,
  styled,
  Input,
  Backdrop,
  Tooltip,
  Avatar
} from '@material-ui/core'
import { AvatarGroup } from '@material-ui/lab'

import { colors } from '../../utils/constants'
import UserAvatar from '../shared/UserAvatar'
import CustomTable from '../table/CustomTable'
import ReactSelect from '../select/ReactSelect'
import StyledSVG from '../shared/StyledSVG'
import MaterialInput from '../shared/MaterialInput'
import { numberFromText } from '../../utils/helpers'
import {
  buildShortText,
  buildTooltipText
} from '../../utils/childrenClassrooms'

import cameraSVG from '../../assets/icons/camera-icon.svg'
import approvedSVG from '../../assets/icons/approved-authorization.svg'
import Modal from '../modal/Modal'
import Button from '../button/Button'

const getChildPerson = child => child?.child || child

const useStyles = makeStyles(theme => ({
  search_inputs: {
    margin: '24px 0px 36px 0px',
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    columnGap: 24,
    rowGap: 32,
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(6, minmax(0, 1fr))'
    },

    [theme.breakpoints.down('1185')]: {
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      columnGap: 12,
      rowGap: 16
    },

    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      columnGap: 0,
      rowGap: 0
    }
  },
  search_field: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
    height: 'fit-content',
    '& .MuiSelect-select.MuiSelect-select': {
      opacity: 0.5
    },
    '& p.label': {
      margin: 0,
      fontSize: 12,
      fontWeight: 600
    },
    [theme.breakpoints.down('600')]: {
      width: '100%'
    }
  },
  material_input_class: {
    width: '100%'
  },
  modal_root: {
    fontFamily: 'Montserrat, Source Sans Pro',
    width: 735,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 48px',
    [theme.breakpoints.down('1025')]: {
      width: '100%',
      padding: '20px'
    }
  },
  modal_header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    '& h1': {
      color: '#386093',
      fontWeight: 600,
      fontSize: 24,
      margin: 0
    },
    '& p': {
      color: '#9B9B9B',
      fontWeight: 400,
      fontSize: 16,
      margin: 0
    }
  },
  childrens_wrapper: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  avatar_wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    '& p': {
      fontWeight: 500,
      fontSize: 16,
      color: '#555555'
    }
  },
  actions_wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 32,
    '& svg': {
      cursor: 'pointer'
    }
  },
  week_days_wrapper: {
    display: 'flex',
    columnGap: 4,
    flexWrap: 'wrap'
  },
  day_wrapper: {
    borderRadius: 8,
    backgroundColor: '#D9E8FA',
    width: 40,
    height: 32,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& p': {
      fontWeight: 500,
      fontSize: 14,
      margin: 0
    }
  },
  reject_input: {
    alignItems: 'flex-start',
    '& input, & textarea': {
      paddingTop: 12,
      paddingLeft: 15
    },
    width: '100%',
    height: 140,
    borderRadius: 8,
    backgroundColor: '#F5F6F7'
  },
  confirm_btn: {
    backgroundColor: '#386093',
    '&:hover': {
      backgroundColor: '#386093'
    }
  },
  root_btn: {
    width: 165,
    color: '#FFFFFF',
    height: 48,
    padding: '14px 48px',
    gap: 10,
    borderRadius: 8,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '19.5px',
    textTransform: 'none !important'
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1
  },
  default_row_style: {
    backgroundColor: '#FFFFFF !important',
    '& td:last-child': {
      borderTopRightRadius: '16px',
      borderBottomRightRadius: '16px'
    },
    '& td:first-child': {
      borderTopLeftRadius: '16px',
      borderBottomLeftRadius: '16px'
    }
  },
  queue_table: {
    '& .MuiTable-root': {
      borderCollapse: 'separate !important',
      borderSpacing: '0px 12px !important'
    }
  },
  photo_preview: {
    position: 'relative',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    width: 72,
    height: 72,
    borderRadius: 24,
    backgroundColor: '#D9D9D9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  responsible_section: {
    margin: '16px 0px 25px 0px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    rowGap: 16,
    '& h1.title': {
      margin: 0,
      color: '#386093',
      fontWeight: 500,
      fontSize: 20,
      textAlign: 'flex-start'
    }
  },
  responsible_wrapper: {
    display: 'flex',
    columnGap: 12,
    alignItems: 'center'
  },
  responsible_description: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    rowGap: 4,
    '& .name': {
      margin: 0,
      fontWeight: 500,
      fontSize: 18
    },
    '& .relationship': {
      margin: 0,
      fontWeight: 500,
      fontSize: 16
    }
  },
  responsible_infos_wrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    rowGap: 4,
    '& .item': {
      display: 'flex',
      alignItems: 'center',
      columnGap: 4,
      '& .label': {
        margin: 0,
        color: '#9B9B9B',
        fontWeight: 400,
        fontSize: 16
      },
      '& .value': {
        margin: 0,
        fontWeight: 500,
        fontSize: 16
      }
    }
  },
  pa_childrens_wrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    rowGap: 16,
    '& h1': {
      margin: 0,
      color: '#386093',
      fontWeight: 500,
      fontSize: 20
    }
  },
  pa_type_wrapper: {
    margin: '16px 0px 20px 0px',
    width: '100%',
    display: 'flex',
    columnGap: 16,
    justifyContent: 'flex-start',
    alignItems: 'center',
    '& h1': {
      margin: 0,
      color: '#386093',
      fontWeight: 500,
      fontSize: 20
    }
  },
  pa_children: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 8
  },
  pa_child: {
    backgroundColor: '#F5F6F7',
    width: '100%',
    borderRadius: 8,
    padding: '8px 32px',
    display: 'flex',
    alignItems: 'center',
    columnGap: 16,
    '& .name': {
      margin: 0,
      fontWeight: 500,
      fontSize: 16
    }
  },
  reject_section: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    '& .subtitle': {
      margin: '8px 0px 16px 0px',
      fontSize: 16,
      fontWeight: 400,
      color: '#9B9B9B'
    }
  },
  reject_title: {
    display: 'flex',
    columnGap: 4,
    '& p': {
      margin: 0,
      fontWeight: 500,
      fontSize: 20
    },
    '& .label': {
      color: '#386093'
    },
    '& .sublabel': {
      color: '#EF7C8F'
    }
  },
  bottom_btns: {
    marginTop: 18,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    [theme.breakpoints.down('769')]: {
      gap: 6
    },
    [theme.breakpoints.down('600')]: {
      gap: 16,
      flexDirection: 'column',
      justifyContent: 'center'
    }
  },
  reject_btn: {
    backgroundColor: '#EF7C8F',
    height: 48,
    padding: '14px 48px',
    borderRadius: 8,
    '&:hover': {
      backgroundColor: '#EF7C8F'
    },
    fontSize: '16px',
    fontWeight: '600 !important',
    textTransform: 'none !important',
    lineHeight: '19.5px'
  },
  approve_btn: {
    backgroundColor: '#386093',
    height: 48,
    padding: '14px 48px',
    borderRadius: 8,
    '&:hover': {
      backgroundColor: '#386093'
    },
    fontSize: '16px',
    fontWeight: '600 !important',
    textTransform: 'none !important',
    lineHeight: '19.5px',
    '&.Mui-disabled': {
      pointerEvents: 'auto',
      cursor: 'pointer'
    }
  },
  cancel_btn: {
    width: '100%',
    border: '1px solid #386093',
    height: 48,
    padding: '14px 48px',
    color: '#386093',
    fontWeight: '600 !important',
    borderRadius: 8,
    '&:hover': {
      border: '1px solid #386093'
    },
    fontSize: '16px',
    textTransform: 'none !important',
    lineHeight: '19.5px'
  },
  grid_btn: {
    width: 200
  },
  react_select: {
    '& .select__control': {
      height: 40
    },
    '& .select__placeholder': {
      fontSize: 14
    },
    [theme.breakpoints.down('345')]: {
      width: 240
    }
  },
  authorization_Type: {
    width: '110px',
    textAlign: 'center'
  }
}))

const authorizationType = {
  permanent: 'Autorização fixa',
  temporary: 'Somente hoje'
}

const FormFieldWrapper = styled('div')(({ theme, gridColumn }) => ({
  gridColumn: `span ${gridColumn || 6}`
}))

const ResponsiblePhoto = styled('div')(({ theme, bgImage }) => ({
  backgroundImage: `url(${bgImage})`
}))

const PickupTypeWrappe = styled('div')(({ theme, bgColor }) => ({
  borderRadius: 8,
  padding: 8,
  backgroundColor: bgColor,
  width: 'fit-content',
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& p': {
    fontSize: 16,
    fontWeight: 500
  }
}))

const AuthorizationType = ({ pickupType }) => {
  const isPermanent = pickupType === 'permanent'
  return (
    <AuthorizationWrapper
      fontColor={isPermanent ? '#FFFFFF' : '#555555'}
      bgColor={isPermanent ? '#00ACDB' : '#F4A87C'}
    >
      <p>{authorizationType[pickupType]}</p>
    </AuthorizationWrapper>
  )
}

const AuthorizationWrapper = styled('div')(({ bgColor, fontColor }) => ({
  height: 40,
  width: 140,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 16px',
  backgroundColor: bgColor,
  '& p': {
    fontWeight: 500,
    fontSize: 14,
    color: fontColor,
    lineHeight: '1em'
  }
}))

const ModalHeader = ({ isPermanent, componentClass }) => {
  const title = isPermanent
    ? 'Analisar autorização fixa'
    : 'Analisar autorização — somente hoje'
  const subTitle = isPermanent
    ? 'Revise os dados antes de aprovar ou rejeitar esta permissão permanente.'
    : 'Esta permissão será válida apenas para o dia de hoje.'
  return (
    <div className={componentClass}>
      <h1>{title}</h1>
      <p>{subTitle}</p>
    </div>
  )
}

const SearchPendingAuthorizationsForm = ({
  getPickupAuthorizations,
  authorizations,
  defaultParams,
  editPickupAuthorizationRSPA,
  approveTemporaryAuthorizationRSPA,
  alert,
  childNameValue,
  responsibleNameValue,
  pickupTypeValue,
  change
}) => {
  const classes = useStyles()
  const [backDrop, setBackdrop] = React.useState({ open: false })
  const [confirmModal, setConfirmModal] = React.useState({
    open: false,
    id: 0,
    reject_message: '',
    data: {}
  })
  const firstPageAfterSearch = React.useRef(false)
  const [searchParams, setSearchParams] = React.useState()
  const columns = [
    {
      key: 'name',
      name: 'Reponsável',
      width: '25%',
      render: row => {
        return (
          <div className={classes.avatar_wrapper}>
            <UserAvatar
              avatarStyle={{
                width: 40,
                height: 40
              }}
              withoutUserName
              userName={row?.name}
              avatarUrl={row?.photo_url}
            />
            <p>{row?.name}</p>
          </div>
        )
      }
    },
    {
      key: 'pickup_type',
      name: 'Tipo',
      width: '13%',
      render: row => {
        return (
          <PickupTypeWrappe
            bgColor={row?.pickup_type === 'permanent' ? '#D9E8FA' : '#F8E7D6'}
          >
            <p className={classes.authorization_Type}>
              {authorizationType[row?.pickup_type]}
            </p>
          </PickupTypeWrappe>
        )
      }
    },
    {
      key: 'vehicle_plate',
      name: 'Placa',
      width: '10%',
      render: row => {
        return <p>{row?.vehicle_plate?.toUpperCase() || '--'}</p>
      }
    },
    {
      key: 'email',
      name: 'Email',
      width: '32%',
      render: row => {
        return <p>{row?.email?.toLowerCase()}</p>
      }
    },
    {
      key: 'childrens',
      name: 'Alunos',
      width: '20%',
      render: row => {
        return (
          <div className={classes.avatar_wrapper}>
            <AvatarGroup max={5} spacing={1}>
              {row?.pickup_authorization_children?.map((pckAuthChild, idx) => {
                const index =
                  numberFromText(pckAuthChild?.child?.name?.slice(0, 2)) %
                  colors.length
                return (
                  <Tooltip
                    key={`child[${idx}]`}
                    title={pckAuthChild?.child?.name}
                    arial-label='avatar-usuario'
                  >
                    <Avatar
                      style={{
                        height: '30px',
                        width: '30px',
                        backgroundColor:
                          // eslint-disable-next-line standard/computed-property-even-spacing
                          colors[index]
                      }}
                      src={pckAuthChild?.child?.avatar_url}
                    >
                      <p className={classes.without_photo_preview}>
                        {pckAuthChild?.child?.name[0]}
                      </p>
                    </Avatar>
                  </Tooltip>
                )
              })}
            </AvatarGroup>
            <p>{row?.pickup_authorization_children?.[0]?.child?.name}</p>
          </div>
        )
      }
    }
  ]

  const handleSearchByFilter = () => {
    const newParams = {
      ...(childNameValue?.length > 0
        ? { 'q[pickup_authorization_children_child_name_cont]': childNameValue }
        : {}),
      ...(responsibleNameValue?.length > 0
        ? { 'q[name_cont]': responsibleNameValue }
        : {}),
      'q[pickup_type_eq]': pickupTypeValue
    }
    getPickupAuthorizations({
      page: 1,
      params: {
        ...defaultParams,
        ...newParams
      }
    })
    setSearchParams(newParams)
    firstPageAfterSearch.current = true
  }

  React.useEffect(
    () => {
      handleSearchByFilter()
    },
    [pickupTypeValue]
  )

  const handleOpenModal = rowData => {
    setConfirmModal(prev => ({ ...prev, open: true, data: rowData }))
  }

  const handleRejectMessage = event => {
    setConfirmModal(prev => ({ ...prev, reject_message: event.target.value }))
  }
  const handleCloseModal = () => {
    setConfirmModal(prev => ({ ...prev, open: false }))
  }

  const handleCloseBackdrop = () => {
    setBackdrop({ open: false })
    getPickupAuthorizations({
      page: 1,
      params: {
        ...defaultParams
      }
    })
  }

  const handleAuthorizationStatus = async (status, id, pickup_type) => {
    const withFeedback = async (promise, reject) => {
      try {
        const res = await promise
        handleCloseModal()
        if (!reject) {
          setBackdrop({ open: true, approved: true })
        } else {
          alert('Autorização rejeitada!', 'success')
        }
        return res
      } catch (error) {
        alert('Erro ao processar autorização.', 'error')
        console.error(error)
      } finally {
        firstPageAfterSearch.current = true
      }
    }
    if (status === 'rejected') {
      await withFeedback(
        editPickupAuthorizationRSPA({
          id,
          data: {
            status: 'rejected',
            rejection_reason: confirmModal?.reject_message
          }
        }),
        true
      )
      getPickupAuthorizations({
        page: 1,
        params: {
          ...defaultParams
        }
      })
    } else {
      let promise
      if (pickup_type === 'temporary') {
        promise = approveTemporaryAuthorizationRSPA(id)
      } else {
        promise = editPickupAuthorizationRSPA({
          id,
          data: {
            status: 'approved'
          }
        })
      }
      await withFeedback(promise, false)
    }
  }

  const applyStyleByRowAttribute = row => {
    return classes.default_row_style
  }

  return (
    <div>
      <div className={classes.search_inputs}>
        <FormFieldWrapper gridColumn={4} className={classes.search_field}>
          <p className='label'>Aluno</p>
          <Field
            rootClass={classes.material_input_class}
            component={MaterialInput}
            name='child_name'
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSearchByFilter()
              }
            }}
            placeholder='Filtrar por aluno'
            type='text'
            autoComplete='off'
          />
        </FormFieldWrapper>
        <FormFieldWrapper gridColumn={4} className={classes.search_field}>
          <p className='label'>Responsável</p>
          <Field
            rootClass={classes.material_input_class}
            component={MaterialInput}
            name='responsible_name'
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSearchByFilter()
              }
            }}
            placeholder='Filtrar por responsável'
            type='text'
            autoComplete='off'
          />
        </FormFieldWrapper>
        <FormFieldWrapper gridColumn={2} className={`${classes.search_field}`}>
          <p className='label'>Tipo de autorização</p>
          <Field
            component={ReactSelect}
            id='authorization_type'
            name='authorization_type'
            placeholder='Selecione o tipo'
            handleChange={(e, i) => change(i.name, e?.value)}
            componentClass={classes.react_select}
            defaultValue={pickupTypeValue}
            isClearable
            options={[
              { label: 'Somente hoje', value: 0 },
              { label: 'Autorização fixa', value: 1 }
            ]}
          />
        </FormFieldWrapper>
      </div>
      <CustomTable
        columns={columns}
        from={'authorizations'}
        data={authorizations.items}
        fetchItems={getPickupAuthorizations}
        params={{ ...defaultParams, ...searchParams }}
        isFetching={authorizations.isFetching}
        pagination={authorizations.pagination}
        classRoot={classes.queue_table}
        onRowClick={handleOpenModal}
        applyStyleByRowAttribute={applyStyleByRowAttribute}
        columnsPointer={[0, 1, 2, 3, 4]}
        border={'none'}
        headCellRadius
        headColor={'rgba(173, 184, 204, 0.3)'}
        firstPageAfterSearch={firstPageAfterSearch}
      />
      <Modal
        open={confirmModal.open}
        handleClose={handleCloseModal}
        disablePadding
      >
        <div className={classes.modal_root}>
          <ModalHeader
            componentClass={classes.modal_header}
            isPermanent={confirmModal?.data?.pickup_type === 'permanent'}
          />
          <div className={classes.responsible_section}>
            <h1 className='title'>Dados do resposável</h1>
            <div className={classes.responsible_wrapper}>
              {confirmModal?.data.photo_url ? (
                <ResponsiblePhoto
                  bgImage={confirmModal?.data.photo_url}
                  className={classes.photo_preview}
                />
              ) : (
                <div className={classes.photo_preview}>
                  <StyledSVG src={cameraSVG} width={32} height={32} />
                </div>
              )}
              <div className={classes.responsible_description}>
                <h1 className='name'>{confirmModal?.data?.name}</h1>
                <p className='relationship'>{confirmModal?.data?.relation}</p>
              </div>
            </div>
            <div className={classes.responsible_infos_wrapper}>
              {[
                { label: 'CPF:', value: 'taxpayer_number' },
                { label: 'Telefone:', value: 'phone' },
                { label: 'Placa do veículo:', value: 'vehicle_plate' },
                { label: 'Email:', value: 'email' }
              ].map(item => {
                return (
                  <div key={item.value} className='item'>
                    <p className='label'>{item.label}</p>
                    <p className='value'>
                      {confirmModal?.data?.[item.value] || '---'}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
          <div className={classes.pa_childrens_wrapper}>
            <h1>Aluno(s) autorizados</h1>
            <div className={classes.pa_children}>
              {confirmModal?.data?.pickup_authorization_children?.map(item => {
                return (
                  <div key={item.id} className={classes.pa_child}>
                    <UserAvatar
                      avatarUrl={item?.child?.avatar_url}
                      avatarStyle={{ width: 48, height: 48 }}
                      userName={item?.child?.name}
                      withoutUserName
                    />
                    <Tooltip
                      title={buildTooltipText(
                        confirmModal?.data?.pickup_authorization_children || [],
                        false,
                        getChildPerson
                      )}
                    >
                      <p className='name'>
                        {confirmModal?.data?.pickup_authorization_children
                          ?.length > 0
                          ? buildShortText(
                              confirmModal?.data?.pickup_authorization_children,
                              48,
                              false,
                              getChildPerson
                            )
                          : '-'}
                      </p>
                    </Tooltip>
                  </div>
                )
              })}
            </div>
          </div>
          <div className={classes.pa_type_wrapper}>
            <h1>Tipo de autorização:</h1>
            <AuthorizationType pickupType={confirmModal?.data?.pickup_type} />
          </div>
          <div className={classes.reject_section}>
            <div className={classes.reject_title}>
              <p className='label'>Justificativa </p>
              <p className='sublabel'>
                (obrigatória apenas em caso de rejeição)
              </p>
            </div>
            <p className='subtitle'>
              Preencha este campo somente se optar por rejeitar a solicitação.
            </p>
            <Input
              disableUnderline
              multiline
              classes={{ root: classes.reject_input }}
              placeholder='Informe o motivo da rejeição desta autorização.'
              onChange={handleRejectMessage}
              value={confirmModal.reject_message}
            />
          </div>
          <div className={classes.bottom_btns}>
            <Button
              rootClass={classes.grid_btn}
              type='button'
              variant='outlined'
              className={classes.cancel_btn}
              onClick={() => {
                handleCloseModal()
              }}
            >
              Cancelar
            </Button>
            <Button
              rootClass={classes.grid_btn}
              type='button'
              className={classes.reject_btn}
              disabled={confirmModal?.reject_message.length === 0}
              onClick={() => {
                handleAuthorizationStatus('rejected', confirmModal.data.id)
              }}
            >
              Rejeitar solicitação
            </Button>
            <Button
              rootClass={classes.grid_btn}
              className={classes.approve_btn}
              onClick={() => {
                handleAuthorizationStatus(
                  'approved',
                  confirmModal.data.id,
                  confirmModal.data.pickup_type
                )
              }}
            >
              Aprovar autorização
            </Button>
          </div>
          {/*  */}
          {/* <Button
            className={`${classes.root_btn} ${classes.cancel_btn}`}
            onClick={handleCloseModal}
          >
            Cancelar
          </Button>
          <Button
            className={`${classes.root_btn} ${classes.confirm_btn}`}
            disabled={confirmModal.reject_message.length === 0}
            onClick={() =>
              handleAuthorizationStatus('rejected', confirmModal.id)
            }
          >
            Confirmar recusa
          </Button> */}
        </div>
      </Modal>
      <Backdrop
        onClick={handleCloseBackdrop}
        className={classes.backdrop}
        open={backDrop.open}
      >
        <StyledSVG src={approvedSVG} height={200} width={300} />
      </Backdrop>
    </div>
  )
}

export default SearchPendingAuthorizationsForm
